import React from "react";
import yaml from "js-yaml";
import { PackageCheck } from "lucide-react";
import { Input } from "components/Graphs/Input";
import { Checkbox } from "components/Graphs/Checkbox";
import BaseContainerSelector from "components/Environment/Create/ContainerSelector/BaseContainerSelector";
import type { ContainerSelected } from "components/Environment/shared/BuildTypes";
import type { NetworkService } from "../networkServices";
import { SectionShell } from "../layout";
import type {
  ManagedServiceStatus,
  RuntimeType,
  SectionControlProps,
} from "../types";

const compactSummaryValue = (value: string, maxLength = 56) => {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;

  const startLength = Math.max(1, maxLength - 18);
  return `${trimmed.slice(0, startLength)}…${trimmed.slice(-15)}`;
};

const joinSummaryParts = (parts: Array<string | false | null | undefined>) =>
  parts.filter((part): part is string => Boolean(part)).join(" • ");

interface ComposeSummaryService {
  name: string;
  ports: string[];
}

const getPublishedComposePort = (port: unknown) => {
  if (typeof port === "string") {
    const portSegments = port.split(":");
    return portSegments[portSegments.length === 3 ? 1 : 0]?.trim() || undefined;
  }

  if (typeof port === "object" && port !== null && "published" in port) {
    const publishedPort = (port as { published?: string | number }).published;
    return publishedPort === undefined ? undefined : String(publishedPort);
  }

  return undefined;
};

const getComposeServicesFromYaml = (
  composeYaml?: string,
): ComposeSummaryService[] => {
  if (!composeYaml?.trim()) return [];

  try {
    const composeConfig = yaml.load(composeYaml) as {
      services?: Record<string, { ports?: unknown[] }>;
    } | null;

    return Object.entries(composeConfig?.services || {}).map(
      ([name, service]) => ({
        name,
        ports: Array.isArray(service?.ports)
          ? service.ports
              .map(getPublishedComposePort)
              .filter((port): port is string => Boolean(port))
          : [],
      }),
    );
  } catch {
    return [];
  }
};

const getComposeServicesFromNetworkServices = (
  services: NetworkService[],
): ComposeSummaryService[] =>
  services
    .filter((service) => service.isEnvironmentManaged && !service.isJupyter)
    .map((service) => ({
      name: service.name,
      ports: service.port ? [service.port] : [],
    }));

const formatComposeServicesPreview = (services: ComposeSummaryService[]) => {
  if (services.length === 0) return undefined;

  const visibleServices = services
    .slice(0, 3)
    .map((service) =>
      service.ports.length > 0
        ? `${service.name} (${service.ports.join(", ")})`
        : service.name,
    );
  const remainingServices =
    services.length > visibleServices.length
      ? `, +${services.length - visibleServices.length} more`
      : "";

  return `Services: ${visibleServices.join(", ")}${remainingServices}`;
};

export const getSoftwareConfigurationSummary = ({
  runtimeType,
  container,
  managedServiceStatus,
  lifecycleScript,
  imageId,
  services,
  pythonVersion,
  cudaVersion,
}: {
  runtimeType: RuntimeType;
  container?: ContainerSelected;
  managedServiceStatus: ManagedServiceStatus;
  lifecycleScript: string;
  imageId: string;
  services: NetworkService[];
  pythonVersion: string;
  cudaVersion: string;
}) => {
  const jupyterStatus =
    managedServiceStatus === "configured" ? "enabled" : "disabled";
  const hasLifecycleScript = Boolean(lifecycleScript.trim());

  if (runtimeType === "VM") {
    return joinSummaryParts([
      "VM mode",
      `Jupyter ${jupyterStatus}`,
      hasLifecycleScript && "Setup script added",
      imageId.trim() ? `Image ID: ${compactSummaryValue(imageId)}` : undefined,
    ]);
  }

  if (runtimeType === "kubernetes") {
    return joinSummaryParts([
      "Single-node Kubernetes",
      `Dashboard ${jupyterStatus}`,
      hasLifecycleScript && "Cluster init script added",
    ]);
  }

  if (container?.customContainer) {
    return joinSummaryParts([
      "Single Container",
      container.customContainer.containerUrl.trim()
        ? `Image: ${compactSummaryValue(container.customContainer.containerUrl)}`
        : undefined,
      container.customContainer.entryPoint.trim()
        ? `Entrypoint: ${compactSummaryValue(
            container.customContainer.entryPoint,
          )}`
        : undefined,
    ]);
  }

  if (container?.dockerCompose) {
    const composeYaml =
      container.dockerCompose.parsedYaml || container.dockerCompose.yamlString;
    const composeServices = getComposeServicesFromYaml(composeYaml);
    const servicesPreview = formatComposeServicesPreview(
      composeServices.length > 0
        ? composeServices
        : getComposeServicesFromNetworkServices(services),
    );
    return joinSummaryParts([
      "Docker Compose",
      container.dockerCompose.fileUrl?.trim()
        ? `Repository: ${compactSummaryValue(container.dockerCompose.fileUrl)}`
        : undefined,
      servicesPreview,
      `Jupyter ${
        container.dockerCompose.jupyterInstall ? "enabled" : "disabled"
      }`,
    ]);
  }

  if (container?.verbBuild) {
    const image =
      container.verbBuild.containerUrl === "Default"
        ? `Featured image with Python ${pythonVersion} and CUDA ${cudaVersion}`
        : `Image: ${compactSummaryValue(container.verbBuild.containerUrl)}`;

    return ["Single Container", image, "Jupyter included"].join(" • ");
  }

  return "Choose a software configuration";
};

export const SoftwareSection: React.FC<
  SectionControlProps & {
    summary: string;
    softwareContainerForSelector: ContainerSelected;
    onContainerChange: (container: ContainerSelected) => void;
    pythonVersion: string;
    onPythonVersionChange: (version: string) => void;
    cudaVersion: string;
    onCudaVersionChange: (version: string) => void;
    lifecycleScriptTab: "upload" | "paste";
    onLifecycleScriptTabChange: (tab: "upload" | "paste") => void;
    lifecycleScriptFile: File | null;
    onLifecycleScriptFileChange: (file: File | null) => void;
    onLifecycleScriptValidationChange: (hasError: boolean) => void;
    isNvidian: boolean;
    runtimeType: RuntimeType;
    imageId: string;
    onImageIdChange: (imageId: string) => void;
    performDefaultBuildForCustomImage: boolean;
    onPerformDefaultBuildForCustomImageChange: (checked: boolean) => void;
    allowFeaturedSingleContainer?: boolean;
  }
> = ({
  summary,
  softwareContainerForSelector,
  onContainerChange,
  pythonVersion,
  onPythonVersionChange,
  cudaVersion,
  onCudaVersionChange,
  lifecycleScriptTab,
  onLifecycleScriptTabChange,
  lifecycleScriptFile,
  onLifecycleScriptFileChange,
  onLifecycleScriptValidationChange,
  isNvidian,
  runtimeType,
  imageId,
  onImageIdChange,
  performDefaultBuildForCustomImage,
  onPerformDefaultBuildForCustomImageChange,
  allowFeaturedSingleContainer = false,
  ...sectionProps
}) => (
  <SectionShell
    id="software"
    title="Software configuration"
    summary={summary}
    icon={<PackageCheck className="h-5 w-5" />}
    {...sectionProps}
  >
    <div className="space-y-6">
      <BaseContainerSelector
        selectedContainer={softwareContainerForSelector}
        onContainerChange={onContainerChange}
        pythonVersion={pythonVersion}
        onPythonVersionChange={onPythonVersionChange}
        cudaVersion={cudaVersion}
        onCudaVersionChange={onCudaVersionChange}
        lifecycleScriptTab={lifecycleScriptTab}
        onLifecycleScriptTabChange={onLifecycleScriptTabChange}
        lifecycleScriptFile={lifecycleScriptFile}
        onLifecycleScriptFileChange={onLifecycleScriptFileChange}
        onValidationChange={onLifecycleScriptValidationChange}
        hideSaveAndContinue
        singleContainerCustomOnly={!allowFeaturedSingleContainer}
        hideCredentials
      />
      {isNvidian && runtimeType === "VM" && (
        <div className="space-y-3">
          <label className="block text-base font-medium text-white">
            Image ID{" "}
            <span className="font-normal text-secondary">(optional)</span>
          </label>
          <Input
            placeholder="projects/<project id>/global/images/<image name>"
            value={imageId}
            onChange={(e) => onImageIdChange(e.target.value)}
            className="mt-1.5"
          />
          <label
            htmlFor="perform-default-build-for-custom-image"
            className="flex items-center gap-2 text-sm text-secondary"
          >
            <Checkbox
              id="perform-default-build-for-custom-image"
              checked={performDefaultBuildForCustomImage}
              disabled={!imageId.trim()}
              onCheckedChange={(checked) =>
                onPerformDefaultBuildForCustomImageChange(checked === true)
              }
            />
            Run default build on this machine image
          </label>
        </div>
      )}
    </div>
  </SectionShell>
);
